import React from "react";
import { Target, Compass, ShieldCheck, Building2 } from "lucide-react";
import SEO from "@/components/common/SEO";
import PageHero from "@/components/common/PageHero";
import SectionHeading from "@/components/common/SectionHeading";
import { Reveal, Stagger, StaggerItem } from "@/components/common/Reveal";
import FinalCTA from "@/components/sections/FinalCTA";
import { contact } from "@/config/site";

const values = [
  { icon: "Target", title: "Requirement-first", copy: "Every engagement starts from your specifications, not a fixed catalogue." },
  { icon: "Compass", title: "Practical coordination", copy: "We connect requirements, suppliers, trading and logistics in one place." },
  { icon: "ShieldCheck", title: "Honest positioning", copy: "No exaggerated claims, no fabricated statistics — only what we genuinely do." },
  { icon: "Building2", title: "International team", copy: "A dedicated team coordinating enquiries across selected international markets." },
];

const iconMap = { Target, Compass, ShieldCheck, Building2 };

export default function About() {
  return (
    <>
      <SEO
        title="About Premier Global Sourcing | International Trade Partner"
        description="Premier Global Sourcing is an international team coordinating product requirements, overseas suppliers and international delivery planning."
        path="/about"
      />
      <PageHero
        eyebrow="About the company"
        title="An international partner for global business"
        intro="Premier Global Sourcing supports businesses that need a practical connection between product requirements, overseas suppliers and international delivery planning."
        breadcrumbs={[{ label: "About Us" }]}
      />

      <section className="container-page py-20 lg:py-28 grid gap-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-7">
          <SectionHeading eyebrow="Who we are" title="One point of coordination for cross-border trade" />
          <div className="mt-6 space-y-5 text-[16px] leading-[27px] text-muted-foreground">
            <p>
              Our international team coordinates trade enquiries involving selected sourcing and commercial
              markets. Our role is to connect the parts of an international purchase that often get
              scattered — the requirement, the suppliers, the commercial terms and the logistics planning.
            </p>
            <p>
              We position ourselves as a coordination partner rather than a courier, factory or freight
              carrier. Where regulated services such as freight, inspection or customs are needed, these
              may be arranged through relevant qualified partners.
            </p>
            <p>
              We are careful about what we claim. You will not find fabricated statistics, ratings or
              testimonials here — only a clear description of how we work and how we can help.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5">
          <div className="rounded-lg border border-[#D9E0E4] bg-white p-7">
            <h3 className="font-heading font-bold text-[18px] text-navy">Company details</h3>
            <dl className="mt-5 space-y-4 text-[14.5px]">
              <div>
                <dt className="text-muted-foreground">Legal name</dt>
                <dd className="text-navy font-medium">Premier Global Sourcing (Private) Limited</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Offices</dt>
                <dd className="text-navy font-medium">{contact.locations.join(" • ")}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Focus</dt>
                <dd className="text-navy font-medium">Sourcing, trading & logistics coordination</dd>
              </div>
            </dl>
          </div>
        </Reveal>
      </section>

      <section className="bg-alt border-y border-[#D9E0E4]">
        <div className="container-page py-20 lg:py-28">
          <SectionHeading eyebrow="What guides us" title="How we approach every requirement" />
          <Stagger className="mt-12 grid gap-px bg-[#D9E0E4] sm:grid-cols-2 lg:grid-cols-4 border border-[#D9E0E4] rounded-lg overflow-hidden">
            {values.map((v) => {
              const Ico = iconMap[v.icon];
              return (
                <StaggerItem key={v.title}>
                  <div className="h-full bg-white p-7">
                    <Ico className="h-6 w-6 text-teal" />
                    <h3 className="mt-5 font-heading font-bold text-[16px] text-navy">{v.title}</h3>
                    <p className="mt-2 text-[14px] leading-relaxed text-muted-foreground">{v.copy}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
